import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import AurBreadcrumb from '../../../common/aur-breadcrumb/AurBreadCrumb';
import {t} from 'i18next';
import AurButton from '../../../common/aur-button/AurButton';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import {
  AurDropdownButton,
  DropdownIconTypes,
} from '../../../common/aur-button/AurDropdownButton';
import {ReactComponent as ExchangeIcon} from '../../../../../src/assests/svg/icon_shift.svg';
import {ReactComponent as AddIcon} from '../../../../../src/assests/svg/add.svg';
import AurSvgIcon from '../../../common/aur-svg-icon/AurSvgIcon';
import {ReactComponent as ErrorIcon} from '../../../../assests/svg/error-field-icon.svg';
import {ReactComponent as BackgroundBlur} from '../../../../assests/svg/backgroundBlur.svg';
import {
  getGridDateOperators,
  GridFeatureMode,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {ecaOverlayResponseType} from '../../../eca-doc-list/eca-doc-list.util';
import {useDispatch, useSelector} from 'react-redux';
import {
  EciHomePageTabs,
  TABLE_PAGE_SIZE_OPTION,
} from '../../../../constants/constants';
import AurPagination from '../../../common/aur-pagination/AurPagination';
import {RootState} from '../../../../redux/store';
import ProjectSwitcherModal from './ProjectSwitcherModal';
import {setModalStatus} from '../../../../redux/slices/invoiceModalSlice';
import {
  downloadResponseAsCSV,
  getProjectReportFileName,
  getUTCDateTimeForDisplay,
} from '../../../../utils/utils';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from '../../../pages/field-management/field-table/FieldPopoverGridFilter';
import {
  exportOverlayCSV,
  exportOverlayErrorCSV,
  getOverlay,
} from '../../../../api/EcaManagementService';
import {
  getFormatedDateForFilter,
  toODataQueryResult,
} from '../../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurDataGridPro, {
  CustomGridColDef,
} from '../../../common/aur-data-grid-pro/AurDataGridPro';
import {AxiosError} from 'axios';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {useNavigate} from 'react-router-dom';
import AurInfographicSeperator from '../../../common/aur-infographic/AurInfographicSeperator';
import AurDataGridNoRowsOverlay from '../../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {debounce, isEmpty} from 'lodash';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  PAGE_TYPE,
  useManageMenuByPermissions,
} from '../../../../hooks/permissions/useManageMenuByPermissions';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import {SecurePage} from '../../../pages/SecurePage';

interface StatusType {
  InProgress: string;
  Failed: string;
  Completed: string;
  CompletedWithError: string;
  Queued: string;
  Retrying: string;
  IndexClosed: string;
}
const DATA_TABLE_FEATURE_MODE: GridFeatureMode = 'server' as GridFeatureMode;

const ManageOverlays: React.FC = () => {
  const {manageMenus} = useManageMenuByPermissions(PAGE_TYPE.Overlay);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterCount, setFilterCount] = useState<number>(0);
  const [overlayData, setOverlayData] = useState<ecaOverlayResponseType[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastODataQueryOverlay = useRef<string | null>(null);
  const [, setIsClearAllLoading] = useState<boolean>(false);
  const {clientId} = useSelector((state: RootState) => state.selectedClient);

  const {projectId, selectedProjectName: projectName} = useSelector(
    (state: RootState) => state.reports
  );

  const selectedProject = useSelector(
    (state: RootState) => state.invoiceModal.modalDropDownSelectedItem
  );

  const {hasOverlayPerm} = usePermissionsByPage(PAGE_NAME.OVERLAYS_MANAGEMENT);

  const {showToast} = useToast();

  const fetchOverlay = useCallback(
    async (ODataQuery: string) => {
      if (lastODataQueryOverlay.current === ODataQuery) return;
      lastODataQueryOverlay.current = ODataQuery;
      setLoading(true);
      try {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&PageNumber=${currentPage}&PageSize=${itemsPerPage}&${ODataQuery}`;
        const response = await getOverlay(paramString);
        const res = response.data;
        const data = res?.data?.[0]?.overlayData || [];

        setTotalItems(res?.meta?.pagination?.totalItems);
        setOverlayData(data);
      } catch (error) {
        console.error('Error fetching overlays:', error);
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [clientId, currentPage, itemsPerPage, projectId, showToast]
  );

  const fetchData = useCallback(() => {
    if (!hasOverlayPerm) return;
    if (!apiRef?.current || !projectId || !clientId) return;

    const filterModel = apiRef.current.state.filter.filterModel;
    const sortModel = apiRef.current.state.sorting.sortModel;
    const uniqueFields = new Set(filterModel.items.map((item) => item.field));
    setFilterCount(uniqueFields.size);
    const updatedGridFilterModel = filterModel
      ? {
          ...filterModel,
          items: filterModel?.items.map((item: any) => {
            return {
              ...item,
              value:
                item.field === 'dateStarted' || item.field === 'dateCompleted'
                  ? getFormatedDateForFilter(item.value)
                  : item.value,
              field: item.field.charAt(0).toUpperCase() + item.field.slice(1),
            };
          }),
        }
      : filterModel;
    const updatedSortModel = sortModel.map((item: any) => {
      return {
        ...item,
        field: item.field.charAt(0).toUpperCase() + item.field.slice(1),
      };
    });
    const ODataQuery = toODataQueryResult(
      updatedGridFilterModel,
      updatedSortModel,
      {
        page: currentPage,
        pageSize: itemsPerPage,
      },
      ['CreatedBy']
    );
    const updatedQuery = ODataQuery.replace(/isAnyOf/g, 'in');
    fetchOverlay(updatedQuery);
  }, [
    apiRef,
    sortModel,
    projectId,
    clientId,
    currentPage,
    itemsPerPage,
    fetchOverlay,
    hasOverlayPerm,
  ]);

  useEffect(() => {
    if (!hasOverlayPerm) return;
    lastODataQueryOverlay.current = null;
    fetchData();
  }, [projectId, clientId, fetchData, hasOverlayPerm]);

  useEffect(() => {
    if (!apiRef?.current || !hasOverlayPerm) return;

    const debouncedFetchData = debounce(() => {
      fetchData();
    }, 1000);

    const handleFilterChange = () => {
      if (currentPage === 1) {
        debouncedFetchData();
      } else {
        setCurrentPage(1);
      }
    };
    const unsubscribe = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterChange
    );
    return () => {
      unsubscribe();
      debouncedFetchData.cancel();
    };
  }, [apiRef, fetchData, hasOverlayPerm]);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const statusText: StatusType = {
    InProgress: t('common.statusTypes.inProgress'),
    Failed: t('common.statusTypes.failed'),
    Completed: t('common.statusTypes.complete'),
    CompletedWithError: t('common.statusTypes.completeWErrors'),
    Queued: t('common.statusTypes.queued'),
    Retrying: t('common.statusTypes.retrying'),
    IndexClosed: t('common.statusTypes.indexClosed'),
  };

  const fieldTypeDataArray: FieldFilterGridData[] = [
    {
      id: 1,
      label: t('common.overlayStatus.inProgress'),
    },
    {
      id: 2,
      label: t('common.overlayStatus.failed'),
    },
    {
      id: 3,
      label: t('common.overlayStatus.completed'),
    },
    {
      id: 4,
      label: t('common.overlayStatus.completedWithErrors'),
    },
    {
      id: 5,
      label: t('common.overlayStatus.queued'),
    },
    {
      id: 6,
      label: t('common.overlayStatus.retrying'),
    },
    {
      id: 7,
      label: t('common.overlayStatus.indexClosed'),
    },
  ];

  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string}[] = [
      {
        label: t('eca.ecaHomepage.coreEciHome'),
        path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
      },
      {
        label: t('eca.overlay.labelOverlay'),
      },
    ];
    return baseItems;
  }, []);

  const computeStatus = (params: GridRenderCellParams) => {
    const getStatus = params.value as keyof StatusType;
    const status =
      getStatus === 'InProgress'
        ? `${params.row.percentComplete}% Complete`
        : statusText[getStatus];

    const errorMessage = params.row?.errorMessage;
    return (
      <>
        <Typography
          className={`typo-text body3_size ${getStatus === 'InProgress' ? 'status-complete' : ''}`}
        >
          {status}
        </Typography>
        {getStatus === 'CompletedWithError' && (
          <AurTooltip title={'Unable to overlay few documents'}>
            <ErrorIcon className="error-icon" tabIndex={0} />
          </AurTooltip>
        )}
        {getStatus === 'Failed' && !isEmpty(errorMessage) && (
          <AurTooltip title={errorMessage}>
            <ErrorIcon className="error-icon" tabIndex={0} />
          </AurTooltip>
        )}
      </>
    );
  };
  const columns: CustomGridColDef[] = [
    {
      field: 'name',
      headerName: t('eca.overlay.overlayName'),
      minWidth: 172,
      flex: 1,
      sortable: true,
    },
    {
      field: 'status',
      headerName: t('eca.overlay.status'),
      minWidth: 100,
      flex: 1,
      sortable: false,
      disableTooltip: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div className="row-container" tabIndex={0}>
            {computeStatus(params)}
          </div>
        );
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'status'}
          apiRef={apiRef}
          columnField={'status'}
          type={'options'}
          dataArray={fieldTypeDataArray}
          inputTabIndex={0}
        />
      ),
    },
    {
      field: 'createdBy',
      headerName: t('eca.overlay.createdBy'),
      minWidth: 100,
      flex: 1,
      sortable: true,
    },
    {
      field: 'dateStarted',
      headerName: t('eca.overlay.dateStarted'),
      minWidth: 100,
      flex: 1,
      sortable: false,
      type: 'date',
      isDateColumn: true,
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (_value, row) => {
        return getUTCDateTimeForDisplay(row.dateStarted);
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'dateStarted'}
          type={'date'}
          inputTabIndex={0}
        />
      ),
    },
    {
      field: 'dateCompleted',
      headerName: t('eca.overlay.dateCompleted'),
      minWidth: 100,
      flex: 1,
      sortable: false,
      type: 'date',
      isDateColumn: true,
      headerClassName: 'no-column-separator',
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (value, row) => {
        if (value == 'Invalid Date') return 'NA';
        return getUTCDateTimeForDisplay(row.dateCompleted);
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'dateCompleted'}
          type={'date'}
        />
      ),
    },
    {
      field: '',
      headerName: '',
      sortable: false,
      filterable: false,
      maxWidth: 20,
      disableTooltip: true,
      renderHeader: () => (
        <span className="sr-only">{t('common.gridColumnHeader')}</span>
      ),
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.gridColumnFilter')}</span>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            {params.row.status === 'CompletedWithError' && (
              <AurLightTooltip title={t('eca.overlay.downloadErrorReportMsg')}>
                <IconButton
                  aria-label={t('eca.overlay.downloadErrorReportHint')}
                  onClick={() => fetchExportOverlayErrorCSVReport(params)}
                >
                  <AurSvgIcon src="images/export_as_csv.svg" ariaHidden />
                </IconButton>
              </AurLightTooltip>
            )}
          </>
        );
      },
    },
  ];

  const projectSwitchHandler = useCallback(() => {
    dispatch(setModalStatus(true));
  }, [dispatch]);

  const renderHeaderNButtons = useMemo(() => {
    return (
      <>
        <Typography
          className="typo-text display3_size overlay-title"
          data-testid="manage-overlay"
        >
          {t('eca.overlay.labelOverlay')}
        </Typography>
        <Box className="button-wrapper">
          {hasOverlayPerm && (
            <AurButton
              ariaLabel={t('eca.overlay.newOverlayHint')}
              classNames="typo-text buttonMedium_size"
              onClick={() => {
                navigate('/eci-home-page/manage-overlays/createoverlay');
              }}
              type="continue"
              size="Medium"
              startIcon={<AddIcon aria-hidden />}
              dataTestId="open-project-workspace-dialog"
            >
              {t('eca.overlay.newOverlay')}
            </AurButton>
          )}
          {manageMenus?.length > 0 && (
            <AurDropdownButton
              buttonText={t('eca.ecaHomepage.manage')}
              type="continue"
              aria-live="polite"
              size="Medium"
              iconType="Arrow"
              iconSize="medium"
              iconClassName="client-profile-details-dropdown-icon"
              menuDirectionLeft={false}
              menuItemHeight="medium"
              menuItemWidth="mediumLarge"
              menuItemTextSize="small"
              dropDownIconType={DropdownIconTypes.Chev}
              menuItems={manageMenus}
              hintText={t('eca.overlay.manageButtonHint')}
            >
              <AurIcon
                icon={IconNames.ChevronDown}
                color={IconColors.Custom}
                customClassName="client-profile-details-dropdown-icon"
              />
            </AurDropdownButton>
          )}

          <AurButton
            ariaLabel={t('eca.overlay.projectButtonHint')}
            classNames="typo-text buttonMedium_size"
            onClick={projectSwitchHandler}
            type="continue"
            size="Medium"
            startIcon={<ExchangeIcon aria-hidden />}
            dataTestId="open-project-workspace-dialog"
          >
            {t('reports.inTakeAuditTab.header.projectButton')}
          </AurButton>
          <ProjectSwitcherModal
            initialSelectedProjectId={projectId ?? selectedProject.id}
            title={t('common.chooseYourProject')}
            heading={t('overlay.manageOverlay')}
          />
        </Box>
      </>
    );
  }, [
    manageMenus,
    navigate,
    projectId,
    projectSwitchHandler,
    selectedProject.id,
    hasOverlayPerm,
  ]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const fetchExportOverlayCSVReport = async () => {
    setIsExporting(true);
    setIsClearAllLoading(true);
    try {
      const paramString = `clientId=${clientId}&projectId=${projectId}`;
      const response = await exportOverlayCSV(paramString);
      downloadResponseAsCSV(
        response,
        getProjectReportFileName(projectName, t('eca.overlay.labelOverlay'))
      );
    } catch (error) {
      console.debug('Handled error:', error); // added log to silent sonar warring
    } finally {
      setIsClearAllLoading(false);
      setIsExporting(false);
    }
  };

  const fetchExportOverlayErrorCSVReport = async (
    params: GridRenderCellParams
  ) => {
    setIsClearAllLoading(true);
    try {
      const overlayId = params.row.id;
      const paramString = `clientId=${clientId}&projectId=${projectId}`;
      const response = await exportOverlayErrorCSV(paramString, overlayId);
      const formattedDate = Date.now();
      downloadResponseAsCSV(response, `${formattedDate}`);
    } catch (error) {
      console.debug('Handled error:', error); // added log to silent sonar warring
    } finally {
      setIsClearAllLoading(false);
    }
  };

  const handlePageSizeChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const renderPaginationBlock = useMemo(() => {
    return (
      <Box className={'table-pagination-container'}>
        <Box className={'table-pagination-control'}>
          <AurPagination
            totalItems={totalItems || 0}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            setItemsPerPage={handlePageSizeChange}
            onPageChange={handlePageChange}
            itemType={t('common.events')}
          />
        </Box>
        {filterCount > 0 ? (
          <>
            <Box>
              <AurInfographicSeperator seperatorIcon={'VerticalLine'} />
            </Box>
            <Box
              className={'apply-filter-button-container'}
              onClick={() => {
                apiRef.current?.setFilterModel({items: []});
              }}
            >
              <AurButton
                type={'exit'}
                size={'Small'}
                startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} />}
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
                ariaLabel={t('common.pagination.clearFiltersHint')}
              />
            </Box>
          </>
        ) : null}
      </Box>
    );
  }, [totalItems, itemsPerPage, currentPage, filterCount, apiRef]);

  const noDataGridOverlay = useMemo(() => {
    if (loading) return AurDataGridLoadingOverlay;
    if (filterCount > 0 && overlayData.length <= 0)
      return AurDataGridNoRowsOverlay;
    return AurDataGridNoRowsOverlay;
  }, [
    filterCount,
    loading,
    overlayData.length,
  ]) as React.JSXElementConstructor<unknown>;

  return (
    <div className="manage-overlays-container">
      <div className="manage-overlays">
        <BackgroundBlur aria-hidden="true" />
        <div className="overlay-layout">
          <Box
            className="breadcrumb-section overlay-breadcrumb"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <AurBreadcrumb items={breadCrumbItems} />
          </Box>

          <Typography
            data-testid="project-title"
            className="typo-text subhead3_size project-title"
          >
            {projectName}
          </Typography>
          <Box display={'flex'} justifyContent={'space-between'}>
            {renderHeaderNButtons}
          </Box>
        </div>
        {hasOverlayPerm === undefined ? (
          <AurLoadingIndicator
            size={16}
            className="progress-icon"
            aria-hidden="true"
          />
        ) : hasOverlayPerm ? (
          <Box className="grid-layout">
            <Box className="grid-header">
              <Box className="grid-title">
                <Typography className="typo-text subhead2_size">
                  {' '}
                  {t('eca.overlay.totalOverlayEvents')}{' '}
                </Typography>
                <Typography className="typo-text body2_size total-count">
                  ({totalItems})
                </Typography>
              </Box>
              <Box>
                <AurButton
                  ariaLabel={`${t('eca.overlay.exportGrid')} ${t('eca.overlay.exportGridHint')}`}
                  onClick={() => fetchExportOverlayCSVReport()}
                  startIcon={
                    isExporting ? (
                      <div className="color-loader">
                        <AurLoadingIndicator
                          size={16}
                          className="progress-icon"
                          aria-hidden="true"
                        />
                      </div>
                    ) : (
                      <AurSvgIcon src="images/export_as_csv.svg" ariaHidden={true} />
                    )
                  }
                  disabled={isExporting}
                  dataTestId="open-project-workspace-dialog"
                  type="exit"
                  size="Medium"
                  label={t('eca.overlay.exportGrid')}
                  classNames="typo-text buttonMedium_size export-btn"
                />
              </Box>
            </Box>
            <Box className="table-container">
              {renderPaginationBlock}
              <AurDataGridPro
                rows={overlayData}
                columns={columns}
                className="listing-data-table "
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 200,
                    },
                  },
                }}
                filterMode={DATA_TABLE_FEATURE_MODE}
                sortingMode={DATA_TABLE_FEATURE_MODE}
                enableServerSide={true}
                checkboxSelection={false}
                getRowId={(row) => row.id}
                apiRef={apiRef}
                disableRowSelectionOnClick
                disableColumnMenu
                headerFilters
                disableColumnFilter
                hideFooter
                visibleToolTip
                columnHeaderHeight={48}
                headerFilterHeight={52}
                rowHeight={50}
                sortModel={sortModel}
                setSortModel={onSetSortModel}
                filterDebounceMs={500}
                slots={{
                  headerFilterMenu: null,
                  noRowsOverlay: noDataGridOverlay,
                  noResultsOverlay: noDataGridOverlay,
                }}
                slotProps={{
                  headerFilterCell: {
                    showClearIcon: false,
                    InputComponentProps: {
                      type: 'search',
                      label: '',
                      placeholder: t('fieldManagement.fieldTable.search'),
                      autoComplete: 'off',
                      tabIndex: '0',
                    },
                  },
                }}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
              />
              {renderPaginationBlock}
            </Box>
          </Box>
        ) : (
          <SecurePage />
        )}
      </div>
    </div>
  );
};

export default ManageOverlays;
